import "./CreatePackage.css";

import { Breadcrumb, Button, Form, Input, message, Select } from "antd";
import { useEffect, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { IoMdAdd } from "react-icons/io";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { type PackageFormType, useAppSelector } from "../../../_lib/_store";
import {
  createPackage,
  deletePackage,
  updatePackage,
} from "../../../_lib/_store/services/packageService";

interface CreatePackagePageProps {
  type: "create" | "edit";
}

export const CreatePackagePage = ({ type }: CreatePackagePageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [tags, setTags] = useState<string[]>([]);
  const { isLoading } = useAppSelector((state) => state.package);
  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";

  useEffect(() => {
    if (isEdit && !state?.package) {
      navigate("/package");
    }
    if (isEdit) {
      form.setFieldsValue(state?.package);
      setTags(state?.package?.tags);
    }
  }, []);

  const handleSubmit = async (values: PackageFormType) => {
    const payload = {
      ...values,
      tags: tags?.filter((tag) => tag.trim() !== ""),
      ...(isEdit ? { id: state.package.id } : {}),
    };

    if (isEdit) {
      const res = await updatePackage(messageApi, payload);
      if (res) {
        navigate("/package/edit", {
          state: { package: res },
          replace: true,
        });
      }
    } else {
      const res = await createPackage(messageApi, payload);
      if (res) {
        form.resetFields();
        setTags([]);
      }
    }
  };

  const handleAddTag = () => {
    setTags([...tags, ""]);
  };
  const handleChangeTag = (index: number, value: string) => {
    const newTags = [...tags];
    newTags[index] = value;
    setTags(newTags);
  };
  const handleRemoveTag = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
  };

  const handleDelete = async () => {
    const res = await deletePackage(messageApi, state.package.id);
    if (res) {
      navigate("/package");
    }
  };

  return (
    <>
      {contextHolder}
      <div className="createPackage">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/package">Package</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <h2 className="title">Create new</h2>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/package/show`, {
                    state: { package: state?.package },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Name is Required" }]}
              tooltip="Package name is required and must be unique"
            >
              <Input size="large" />
            </Form.Item>
            <Form.Item label="Description" name="description">
              <Input size="large" />
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <div className="tagBox">
                {tags?.map((v, i) => {
                  return (
                    <div key={i} className="tagInputBox">
                      <p>{i + 1}</p>
                      <Input
                        className="tagInput"
                        size="large"
                        value={v}
                        onChange={(e) => {
                          handleChangeTag(i, e.target.value);
                        }}
                      />
                      <BsTrash
                        className="trashIcon"
                        onClick={() => {
                          handleRemoveTag(i);
                        }}
                      />
                    </div>
                  );
                })}

                <Button
                  icon={<IoMdAdd />}
                  size="large"
                  className="addButton"
                  onClick={handleAddTag}
                >
                  Add new Item
                </Button>
              </div>
            </Form.Item>
            <Form.Item
              label="Package Type"
              name="packageType"
              rules={[{ required: true, message: "Package Type is Required" }]}
              tooltip="Note: only one package can have Verification or Certification type"
            >
              <Select
                size="large"
                options={[
                  {
                    value: "Verification Package",
                    label: "Verification Package",
                  },
                  {
                    value: "Certification Package",
                    label: "Certification Package",
                  },
                  {
                    value: "Other Package and Module",
                    label: "Other Package and Module",
                  },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: "150px" }}
                loading={isLoading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
